import {
  addDays,
  endOfDay,
  format,
  getDay,
  isAfter,
  isWithinInterval,
} from "date-fns";
import { I18LanguagesEnum } from "../enums";
import { localizedNumber } from "../help/localization/localization";
import { getDateWithTime } from "../help/offers/offers";
import { formatTimeAMPMLocalized } from "../help/textPreparation/textPreparation";
import { hasBuyRequirements, isBundleShaped } from "../loyalty/offerShape";
import {
  EntityImageLookup,
  IsLoyaltyOfferApplicableArgs,
  LoyaltyApplicationLevel,
  LoyaltyChannel,
  LoyaltyOfferApplicabilityResult,
  LoyaltyOfferApplicationScope,
  LoyaltyOfferCategory,
  LoyaltyOfferGraphic,
  LoyaltyOfferInapplicableReason,
  LoyaltyOfferReasonDescriptor,
  LoyaltyOfferSystem,
  LoyaltyOfferType,
  LoyaltyOfferValidity,
  LoyaltyOrderType,
  LoyaltyRewardType,
  LoyaltyTargetConfig,
  LoyaltyTargetType,
  LoyaltyValueType,
  OfferIconUi,
  OfferUiConfigResolved,
} from "../loyaltyTypes";
import { mapWeekdayToLoyaltyDayOfWeek } from "../LoyaltyUtils/loyaltyMapper";

export default abstract class LoyaltyOffer {
  id: string;
  shortId?: string;
  label?: string;
  rewardType: LoyaltyRewardType;
  offerCategory: LoyaltyOfferCategory;
  valueType: LoyaltyValueType;
  value: number;
  maxRewardAmount?: number;
  applicationLevel: LoyaltyApplicationLevel;
  uiConfig?: OfferUiConfigResolved;
  applicationScope: LoyaltyOfferApplicationScope;
  isActive: boolean;
  validity: LoyaltyOfferValidity;
  itemLevelConfig?: LoyaltyTargetConfig;
  channels?: LoyaltyChannel[];
  orderTypes?: LoyaltyOrderType[];
  milestoneId?: string;
  tierId?: string;
  milestonePoints?: number;
  offerSystem: LoyaltyOfferSystem;

  protected constructor(offer: LoyaltyOfferType) {
    this.id = offer.id;
    this.shortId = offer.shortId;
    this.label = offer.label;
    this.rewardType = offer.rewardType;
    this.offerCategory = offer.offerCategory;
    this.valueType = offer.valueType;
    this.value = offer.value;
    this.maxRewardAmount = offer.maxRewardAmount;
    this.applicationLevel = offer.applicationLevel;
    this.uiConfig = offer.uiConfig;
    this.applicationScope = offer.applicationScope;
    this.isActive = offer.isActive;
    this.validity = offer.validity;
    this.itemLevelConfig = offer.itemLevelConfig;
    this.channels = offer.channels;
    this.orderTypes = offer.orderTypes;
    this.milestoneId = offer.milestoneId;
    this.tierId = offer.tierId;
    this.milestonePoints = offer.milestonePoints;
    this.offerSystem = offer.offerSystem;
  }

  isCartOffer(): boolean {
    return false;
  }

  isItemOffer(): boolean {
    return false;
  }

  isDiscountOffer(): boolean {
    return this.rewardType === LoyaltyRewardType.DISCOUNT;
  }

  isPointsOffer(): boolean {
    return this.rewardType === LoyaltyRewardType.POINTS;
  }

  isPercentageValueType(): boolean {
    return this.valueType === LoyaltyValueType.PERCENTAGE;
  }

  isFixedValueType(): boolean {
    return this.valueType === LoyaltyValueType.FIXED;
  }

  isNewValueType(): boolean {
    return this.valueType === LoyaltyValueType.NEW_VALUE;
  }

  hasMaxRewardAmount(): boolean {
    return Boolean(this.maxRewardAmount);
  }

  hasMinimumSpend(): boolean {
    return this.validity.minimumSpendAmount > 0;
  }

  getExpiryDate(): string | undefined {
    const endDate = this.validity?.dateRange?.end?.date;
    if (!endDate) {
      return undefined;
    }

    return format(endDate, "dd-MM-yyyy");
  }

  isExpiryWithinDays(days = 60): boolean {
    const expiryDate = this.validity.dateRange?.end?.date;
    if (!expiryDate) {
      return false;
    }

    return isWithinInterval(new Date(expiryDate), {
      start: new Date(),
      end: addDays(new Date(), days),
    });
  }

  isExpired(): boolean {
    const end = this.validity?.dateRange?.end;
    if (!end?.date) {
      return false;
    }

    // The offer is valid through its end time (end of day when no time is set),
    // matching the backend's `end_time ?? '23:59:59'` fallback. Never compare
    // against getExpiryDate(): its dd-MM-yyyy display string parses as MM-DD.
    const endOfValidity = end.time
      ? getDateWithTime(new Date(end.date), end.time)
      : endOfDay(new Date(end.date));

    return isAfter(new Date(), endOfValidity);
  }

  isTimedApplicable(timeZone: string): boolean {
    const dailyTime = this.validity.dailyTime;
    if (!dailyTime?.start || !dailyTime.end) {
      return true;
    }

    const now = new Date();
    const franchiseTime = now.toLocaleString("en-US", { timeZone });
    const nowWithFranchiseTimezone = new Date(franchiseTime);

    const offerStartDate = getDateWithTime(
      nowWithFranchiseTimezone,
      dailyTime.start
    );
    const offerEndDate = getDateWithTime(
      nowWithFranchiseTimezone,
      dailyTime.end
    );

    return isWithinInterval(nowWithFranchiseTimezone, {
      start: offerStartDate,
      end: offerEndDate,
    });
  }

  isDayOfWeekApplicable(timeZone: string): boolean {
    if (!this.validity.validDaysOfWeek?.length) {
      return true;
    }

    const now = new Date();
    const franchiseTime = now.toLocaleString("en-US", { timeZone });
    const nowWithFranchiseTimezone = new Date(franchiseTime);
    const currentDay = mapWeekdayToLoyaltyDayOfWeek(
      getDay(nowWithFranchiseTimezone)
    );

    return this.validity.validDaysOfWeek.includes(currentDay);
  }

  isApplicableForOrderType(orderType?: LoyaltyOrderType): boolean {
    if (!this.orderTypes?.length || !orderType) {
      return true;
    }

    return this.orderTypes.includes(orderType);
  }

  isApplicableForChannel(channel?: LoyaltyChannel): boolean {
    if (!this.channels?.length || !channel) {
      return true;
    }

    return this.channels.includes(channel);
  }

  getDailyTimeTextRange(t: (text: string) => string): string | undefined {
    if (this.validity.dailyTime?.start && this.validity.dailyTime?.end) {
      return `from ${formatTimeAMPMLocalized(
        t,
        this.validity.dailyTime.start
      )} to ${formatTimeAMPMLocalized(t, this.validity.dailyTime.end)}`;
    }

    if (this.validity.dailyTime?.start) {
      return `from ${formatTimeAMPMLocalized(
        t,
        this.validity.dailyTime.start
      )}`;
    }

    return this.validity.dailyTime?.end
      ? `until ${this.validity.dailyTime.end}`
      : undefined;
  }

  getInapplicabilityDescriptor(
    reason?: LoyaltyOfferInapplicableReason
  ): LoyaltyOfferReasonDescriptor | undefined {
    if (!reason) {
      return undefined;
    }

    switch (reason) {
      case LoyaltyOfferInapplicableReason.DAILY_TIME_OUTSIDE_WINDOW:
        return {
          key: "offers:offer-unavailable",
        };

      case LoyaltyOfferInapplicableReason.EXPIRED:
        return { key: "offers:offer-expired" };

      case LoyaltyOfferInapplicableReason.INVALID_DAY_OF_WEEK:
        return { key: "offers:offer-unavailable" };

      case LoyaltyOfferInapplicableReason.INVALID_ORDER_TYPE:
        return { key: "offers:offer-unavailable" };

      case LoyaltyOfferInapplicableReason.INVALID_CHANNEL:
        return { key: "offers:offer-unavailable" };

      case LoyaltyOfferInapplicableReason.ITEMS_UNAVAILABLE:
        return { key: "common:items-unavailable" };

      case LoyaltyOfferInapplicableReason.INACTIVE:
      default:
        return { key: "offers:offer-unavailable" };
    }
  }

  /** Whether the offer cannot be redeemed on this surface (drives locked styling, apply CTA, alert chip). */
  static isLoyaltyOfferBlocked(
    disabled: boolean,
    applicable: boolean,
    hasDeletedMenuItems: boolean
  ): boolean {
    return disabled || !applicable || hasDeletedMenuItems;
  }

  /**
   * Cart card / flash banner chip text. When the offer is available, shows optional
   * conditions text; when blocked, copy comes only from {@link LoyaltyOfferApplicabilityResult.descriptor}
   * (see `LoyaltyOffer.getInapplicabilityDescriptor`) so i18n keys live in one place.
   */
  static getLoyaltyOfferStatusChipText(
    isOfferUnavailable: boolean,
    applicabilityResult: LoyaltyOfferApplicabilityResult,
    t: (key: string) => string,
    eligibleOfferDetails?: string
  ): string | undefined {
    if (!isOfferUnavailable) {
      return eligibleOfferDetails;
    }

    return t(applicabilityResult.descriptor?.key ?? "offers:offer-unavailable");
  }

  isOfferApplicable(
    args: IsLoyaltyOfferApplicableArgs
  ): LoyaltyOfferApplicabilityResult {
    if (!this.isActive) {
      const reason = LoyaltyOfferInapplicableReason.INACTIVE;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (this.isExpired()) {
      const reason = LoyaltyOfferInapplicableReason.EXPIRED;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (!this.isTimedApplicable(args.timeZone)) {
      const reason = LoyaltyOfferInapplicableReason.DAILY_TIME_OUTSIDE_WINDOW;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (!this.isDayOfWeekApplicable(args.timeZone)) {
      const reason = LoyaltyOfferInapplicableReason.INVALID_DAY_OF_WEEK;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (!this.isApplicableForOrderType(args.orderType)) {
      const reason = LoyaltyOfferInapplicableReason.INVALID_ORDER_TYPE;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (!this.isApplicableForChannel(args.channel)) {
      const reason = LoyaltyOfferInapplicableReason.INVALID_CHANNEL;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    if (this.hasDeletedItems(args.entityImageLookup)) {
      const reason = LoyaltyOfferInapplicableReason.ITEMS_UNAVAILABLE;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    return {
      applicable: true,
    };
  }

  isEqual(offer: LoyaltyOffer | undefined): boolean {
    if (!offer) {
      return false;
    }

    return this.id === offer.id && this.milestoneId === offer.milestoneId;
  }

  /**
   * The single menu sub-item uuid this offer instance targets, or undefined
   * when the instance is not item-specific (multiple choices / category
   * target). Split milestone instances repeat the same offer id with one
   * target entity each, so this is what tells the instances apart.
   */
  getSingleTargetItemEntityId(): string | undefined {
    const config = this.itemLevelConfig;
    if (
      config?.targetType !== LoyaltyTargetType.ITEMS ||
      config.targetEntities.length !== 1
    ) {
      return undefined;
    }

    return config.targetEntities[0].entityId;
  }

  /** Menu entity ids on this offer no longer exist in the loaded menu (compare {@link EntityImageLookup}). */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- base stub; {@link LoyaltyItemOffer} uses the argument
  hasDeletedItems(entityImageLookup?: EntityImageLookup): boolean {
    return false;
  }

  getTitle(language: keyof OfferUiConfigResolved["title"]): string {
    return this.uiConfig?.title?.[language] ?? "";
  }

  getSubTitle(language: keyof OfferUiConfigResolved["title"]): string {
    return this.uiConfig?.subTitle?.[language] ?? "";
  }

  getDescription(language: keyof OfferUiConfigResolved["title"]): string {
    return this.uiConfig?.description?.[language] ?? "";
  }

  /** Pre-formatted expiry line from the loyalty service, e.g. "Valid until 1 May 2025". */
  getValidUntil(
    language: keyof OfferUiConfigResolved["title"]
  ): string | undefined {
    return this.uiConfig?.validUntil?.[language];
  }

  getConditions(language: keyof OfferUiConfigResolved["title"]): string[] {
    return (
      this.uiConfig?.conditions?.map(
        (condition) => condition?.[language] ?? ""
      ) ?? []
    );
  }

  getCartCardLabel(language: keyof OfferUiConfigResolved["title"]): string {
    return this.uiConfig?.cartCard?.label?.[language] ?? "";
  }

  getCartCardSubText(language: keyof OfferUiConfigResolved["title"]): string {
    return this.uiConfig?.cartCard?.subText?.[language] ?? "";
  }

  getLockedText(language: I18LanguagesEnum, points?: number): string {
    const text = this.uiConfig?.popup?.lockedText?.[language] ?? "";
    if (points === undefined) {
      return text;
    }

    return text.replace("{points}", localizedNumber(points));
  }

  getIcon(): OfferIconUi | undefined {
    return this.uiConfig?.icon;
  }

  isMultiplierOffer(): boolean {
    return (
      (this.offerCategory === LoyaltyOfferCategory.ITEM_POINTS ||
        this.offerCategory === LoyaltyOfferCategory.CART_POINTS ||
        this.offerCategory === LoyaltyOfferCategory.CATEGORY_POINTS) &&
      this.valueType === LoyaltyValueType.PERCENTAGE
    );
  }

  getGraphic(): LoyaltyOfferGraphic | undefined {
    return this.uiConfig?.graphics?.[0];
  }

  isItemFromListOffer(): boolean {
    return false;
  }

  /** Anything with a buy side to satisfy first — see `utils/loyalty/offerShape`. */
  isBogoOffer(): boolean {
    return hasBuyRequirements(this);
  }

  /** A fixed number of items, above one, sharing one pooled reward. */
  isBundleOffer(): boolean {
    return isBundleShaped(this.itemLevelConfig);
  }

  /** Plain shape for Redux payloads (`redeemLoyaltyOffer`, `setOfferData`, etc.). */
  toPlain(): LoyaltyOfferType {
    return {
      id: this.id,
      shortId: this.shortId,
      label: this.label,
      rewardType: this.rewardType,
      offerCategory: this.offerCategory,
      valueType: this.valueType,
      value: this.value,
      maxRewardAmount: this.maxRewardAmount,
      applicationLevel: this.applicationLevel,
      uiConfig: this.uiConfig,
      applicationScope: this.applicationScope,
      isActive: this.isActive,
      validity: this.validity,
      itemLevelConfig: this.itemLevelConfig,
      channels: this.channels,
      orderTypes: this.orderTypes,
      milestoneId: this.milestoneId,
      tierId: this.tierId,
      milestonePoints: this.milestonePoints,
      offerSystem: this.offerSystem,
    };
  }
}
